import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';

/**
 * Search Spotlight Component
 *
 * @slot - Default slot for spotlight groups and items
 */
@customElement('te-search-spotlight')
export class TeSearchSpotlight extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .spotlight-wrapper {
        background-color: ${theme.colors.shade0};
        box-shadow: ${theme.shadows.secondary};
        display: flex;
        flex-direction: column;
        width: fit-content;
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        border-radius: 4px;
        min-width: 256px;
      }

      .spotlight-scroller {
        display: flex;
        flex-direction: column;
        width: 100%;
        overflow: auto;
      }

      .spotlight-footer {
        height: 8px;
        box-shadow: ${theme.shadows.top};
        z-index: 1;
        margin-top: -1px;
      }

      .spotlight-no-content {
        padding-left: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 128px;
      }
    `,
  ];

  /** Text value when no results are available */
  @property({ type: String }) noResultsText = 'No Results Found!';
  /** Maximum height for search spotlight */
  @property({ type: Number }) maxHeight = 512;

  @state() private _slotHasContent = false;

  @query('#spotlight-content-slot') _slotContent?: HTMLSlotElement;

  getSlottedNodes(): Node[] {
    if (!this._slotContent) return [];
    return this._slotContent.assignedNodes({ flatten: true });
  }

  checkIfSlotHasContent() {
    const nodes = this.getSlottedNodes();
    const hasChildren = nodes.some(
      (node) => node.nodeName === 'TE-SPOTLIGHT-GROUP' || node.nodeName === 'TE-SPOTLIGHT-ITEM'
    );
    this._slotHasContent = hasChildren;
  }

  styleFirstGroupTitle() {
    const nodes = this.getSlottedNodes();
    const node = nodes.find((n) => n.nodeName === 'TE-SPOTLIGHT-GROUP') as HTMLElement;
    if (!node || !node.shadowRoot) return;
    const firstGroupTitle = node.shadowRoot.querySelector('.group-title');
    if (!firstGroupTitle) return;
    firstGroupTitle.classList.add('first-group-title');
  }

  updated() {
    this.checkIfSlotHasContent();
    this.styleFirstGroupTitle();
  }

  renderNoContent() {
    if (this._slotHasContent) return '';
    return html`
      <div class="spotlight-no-content">${this.noResultsText}</div>
    `;
  }

  renderFooter() {
    if (this._slotHasContent)
      return html`
        <div class="spotlight-footer"></div>
      `;
    return;
  }

  render() {
    return html`
      <div class="spotlight-wrapper">
        <div class="spotlight-scroller" style="max-height:${this.maxHeight - 8}px;">
          <slot id="spotlight-content-slot"></slot>
        </div>
        ${this.renderNoContent()} ${this.renderFooter()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-search-spotlight': TeSearchSpotlight;
  }
}
